import { Shield, Key, Lock, Globe, RefreshCw, TrendingUp, Trash2, Smartphone } from 'lucide-react'
import { useScrollAnimation } from '../hooks/useScrollAnimation'

const securityItems = [
  { icon: Shield, title: 'Two-Factor Auth (2FA)', desc: 'Add an extra layer of protection with authenticator app support.' },
  { icon: Key, title: 'JWT Authentication', desc: 'Stateless, secure session tokens that expire automatically.' },
  { icon: Lock, title: 'bcrypt Passwords', desc: 'Passwords are hashed and salted — never stored in plain text.' },
  { icon: Globe, title: 'HTTPS Everywhere', desc: 'All traffic encrypted in transit. No exceptions.' },
]

const trustBadges = [
  { icon: RefreshCw, title: 'Brokerage credentials never stored', desc: 'SnapTrade OAuth integration means we only ever receive read-only tokens.' },
  { icon: TrendingUp, title: 'Real-time data via Financial Modeling Prep', desc: 'Dividend data, ex-dates, and pricing sourced from FMP.' },
  { icon: Trash2, title: 'Full data deletion on cancellation', desc: 'If you ever leave, your data is permanently deleted.' },
  { icon: Smartphone, title: 'Works on mobile', desc: 'Fully responsive web app — plus native iOS and Android apps available on the App Store and Google Play.' },
]

export default function SecuritySection() {
  const gridRef = useScrollAnimation({ childSelector: '.security-item', stagger: 0.15 })
  const badgesRef = useScrollAnimation({ childSelector: '.trust-badge', stagger: 0.1 })

  return (
    <section className="bg-white section-padding">
      <div className="content-max">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          {/* Left: Header */}
          <div>
            <p className="text-xs font-medium tracking-widest uppercase text-[#1B5E20] mb-4">Your data stays yours. Always.</p>
            <h2 className="font-display text-4xl lg:text-5xl text-[#0F172A] leading-tight mb-6">
              We built security in from day one.
            </h2>
            <p className="text-base text-[#475569] leading-relaxed max-w-md">
              HTTPS everywhere, encrypted passwords, JWT authentication, and 2FA — because your financial data deserves the same protection as your bank.
            </p>
          </div>

          {/* Right: Security Grid */}
          <div ref={gridRef} className="grid sm:grid-cols-2 gap-6">
            {securityItems.map((item) => {
              const Icon = item.icon
              return (
                <div key={item.title} className="security-item">
                  <div className="p-2.5 bg-[#F0FDF4] rounded-lg inline-block mb-3">
                    <Icon className="w-7 h-7 text-[#1B5E20] stroke-[1.5]" />
                  </div>
                  <h4 className="text-lg font-medium text-[#0F172A] mb-1">{item.title}</h4>
                  <p className="text-sm text-[#475569] leading-relaxed">{item.desc}</p>
                </div>
              )
            })}
          </div>
        </div>

        {/* Trust Badges Row */}
        <div ref={badgesRef} className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {trustBadges.map((badge) => {
            const Icon = badge.icon
            return (
              <div key={badge.title} className="trust-badge flex items-start gap-3 p-4 bg-[#F0FDF4] rounded-lg border border-[#DCFCE7]">
                <Icon className="w-5 h-5 text-[#1B5E20] stroke-[1.5] shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-[#0F172A] mb-0.5">{badge.title}</p>
                  <p className="text-xs text-[#475569]">{badge.desc}</p>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
